/**
 * @voltxampere/presets — RC phase-shift oscillator (Sprint 50-4)
 *
 * Three-stage RC ladder + inverting op-amp produces sinusoidal
 * oscillation when total feedback phase shift = 360°. Each RC
 * section contributes ~60° at f₀ = 1/(2π·R·C·√6). With R=10kΩ +
 * C=10nF → f₀ ≈ 650 Hz.
 *
 * Op-amp gain must be ≥29 to satisfy Barkhausen criterion (the
 * RC network attenuates by 1/29 at f₀).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const rcPhaseShiftOsc = {
    id: "rc-phase-shift-osc",
    name: { tr: "RC Faz Kayma Osilatörü", en: "RC Phase-Shift Oscillator" },
    category: "oscillator",
    difficulty: 4,
    description: {
        tr: "3-aşamalı RC ağı + ters çeviren op-amp. f₀ = 1/(2π·R·C·√6) ≈ 650 Hz.",
        en: "3-stage RC ladder + inverting op-amp. f₀ = 1/(2π·R·C·√6) ≈ 650 Hz.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "RC Phase-Shift Oscillator",
        description: "3-stage RC ladder + op-amp",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "n1" }),
            makeNet({ name: "n2" }),
            makeNet({ name: "v_in" }),
            makeNet({ name: "v_out" }),
        ],
        components: [
            makeComponent({
                id: "V_CC",
                type: "voltage-source",
                params: { voltage: 12 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V_CC",
                position: { x: -240, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "U1",
                type: "opamp",
                params: { gain: 30, vSat: 11 },
                terminals: { in_pos: "net_gnd", in_neg: "net_v_in", out: "net_v_out" },
                label: "U1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            // 3-stage RC ladder: out → n1 → n2 → v_in
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_v_out", p2: "net_n1" },
                label: "C1",
                position: { x: 80, y: 80 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_n1", p2: "net_gnd" },
                label: "R1",
                position: { x: 120, y: 120 },
                rotation: 90,
            }),
            makeComponent({
                id: "C2",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_n1", p2: "net_n2" },
                label: "C2",
                position: { x: 160, y: 80 },
                rotation: 0,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_n2", p2: "net_gnd" },
                label: "R2",
                position: { x: 200, y: 120 },
                rotation: 90,
            }),
            makeComponent({
                id: "C3",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_n2", p2: "net_v_in" },
                label: "C3",
                position: { x: 240, y: 80 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_FB",
                type: "resistor",
                params: { resistance: 290000 },
                terminals: { p1: "net_v_in", p2: "net_v_out" },
                label: "R_FB",
                position: { x: 0, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -240, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [],
    }),
    expectedValues: [
        {
            node: "v_out",
            analysis: "dc",
            value: 0,
            tolerance: 1.0,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Üç RC kademesi 180° faz, op-amp 180° → toplam 360°.",
            en: "Three RC stages give 180° phase, op-amp adds 180° → 360° total.",
        },
        {
            tr: "Op-amp kazancı ≥29 osilasyon koşulu.",
            en: "Op-amp gain ≥29 for sustained oscillation.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, §17.3"],
};
//# sourceMappingURL=rc-phase-shift-osc.js.map